import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, XCircle, Trash2, Activity, Zap, Calendar, Clock, RefreshCw, Target } from 'lucide-react';
import { targetsAPI, scansAPI, modulesAPI } from '../services/api';
import { getModuleDisplayName } from '../utils/moduleNames';
import { formatScanDuration } from '../utils/dateFormat';
import ScanModal from '../components/Scans/ScanModal';
import PageHeader from '../components/common/PageHeader';
import ConfirmModal from '../components/common/ConfirmModal';

const TargetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [scans, setScans] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ isOpen: false, type: null, scanId: null });

  useEffect(() => {
    fetchTargetAndScans();
    const interval = setInterval(fetchTargetAndScans, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTargetAndScans = async () => {
    try {
      const [targetRes, scansRes, modulesRes] = await Promise.all([
        targetsAPI.get(id),
        scansAPI.list(id),
        modulesAPI.list()
      ]);
      setTarget(targetRes.data);
      setScans(scansRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'status-pending',
      running: 'status-running',
      completed: 'status-completed',
      failed: 'status-failed'
    };
    return colors[status] || 'status-pending';
  };

  const getStatusIcon = (status) => {
    if (status === 'running') {
      return <RefreshCw className="w-4 h-4 animate-spin" />;
    }
    return null;
  };

  const handleCancelClick = (scanId, e) => {
    e.stopPropagation();
    setConfirmAction({ isOpen: true, type: 'cancel', scanId });
  };

  const handleCancelScan = async () => {
    try {
      await scansAPI.cancel(confirmAction.scanId);
      await fetchTargetAndScans();
    } catch (error) {
      console.error('Failed to cancel scan:', error);
      alert('Failed to cancel scan. Please try again.');
    }
  };

  const handleDeleteClick = (scanId, e) => {
    e.stopPropagation();
    setConfirmAction({ isOpen: true, type: 'delete', scanId });
  };

  const handleDeleteScan = async () => {
    try {
      await scansAPI.delete(confirmAction.scanId);
      await fetchTargetAndScans();
    } catch (error) {
      console.error('Failed to delete scan:', error);
      alert('Failed to delete scan. Please try again.');
    }
  };

  const handleConfirm = () => {
    if (confirmAction.type === 'cancel') {
      handleCancelScan();
    } else if (confirmAction.type === 'delete') {
      handleDeleteScan();
    }
  };

  const handleSaveScan = async (formData) => {
    const dataWithTarget = { ...formData, target_id: parseInt(id) };
    await scansAPI.create(dataWithTarget);
    await fetchTargetAndScans();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-shinobi-purple">Loading...</div>
      </div>
    );
  }

  if (!target) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-xl text-slate-500">Target not found</h2>
          <button
            onClick={() => navigate('/targets')}
            className="mt-4 btn-cyber px-4 py-2 rounded"
          >
            Back to Targets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={target.name || target.value}
        subtitle={target.type?.toUpperCase()}
        icon={Target}
        showBackButton={true}
        onBack={() => navigate('/targets')}
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-cyber px-4 py-2 rounded flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Scan</span>
          </button>
        }
      />
      <div className="p-8">

      {/* Scans List */}
      <div className="cyber-card rounded-lg p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">Scans</h2>
        
        {scans.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No scans yet. Start your first scan.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {scans.map((scan) => (
              <div
                key={scan.id}
                onClick={() => {
                  if (scan.status === 'completed') {
                    navigate(`/scans/${scan.id}/results`, { state: { from: `/targets/${id}` } });
                  }
                }}
                className={`flex items-start p-5 bg-white rounded-lg border border-slate-200 hover:border-slate-200 transition-all ${
                  scan.status === 'completed' ? 'cursor-pointer hover:scale-[1.01]' : ''
                }`}
              >
                <div className="flex items-start flex-1">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                    scan.scan_type === 'passive'
                      ? 'bg-shinobi-purple/10 border border-shinobi-purple/30'
                      : 'bg-shinobi-orange/10 border border-shinobi-orange/30'
                  }`}>
                    {scan.scan_type === 'passive' ? (
                      <Zap className="w-6 h-6 text-shinobi-purple" />
                    ) : (
                      <Activity className="w-6 h-6 text-shinobi-orange" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-bold text-slate-800">
                        {getModuleDisplayName(scan.module_name)}
                      </h3>
                      <span className={`px-3 py-1 rounded text-xs font-semibold flex items-center space-x-1 ${getStatusColor(scan.status)}`}>
                        {getStatusIcon(scan.status)}
                        <span>{scan.status}</span>
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        scan.scan_type === 'passive' ? 'bg-shinobi-purple/10 text-shinobi-purple' : 'bg-shinobi-orange/10 text-shinobi-orange'
                      }`}>
                        {scan.scan_type === 'passive' ? 'GhostPulse' : 'NoxScan'}
                      </span>
                    </div>

                    {(() => {
                      const module = modules.find(m => m.name === scan.module_name);
                      return module && (
                        <p className="text-sm text-gray-500 mb-3">{module.description}</p>
                      );
                    })()}

                    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-3 h-3" />
                        <span>Started {new Date(scan.created_at).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-3 h-3" />
                        <span>Duration: {formatScanDuration(scan.created_at, scan.completed_at)}</span>
                      </div>
                      {scan.status === 'completed' && scan.results_count !== undefined && (
                        <div className="flex items-center space-x-2">
                          <span className="text-shinobi-purple">•</span>
                          <span className="text-shinobi-purple font-semibold">{scan.results_count} results found</span>
                        </div>
                      )}
                    </div>

                    {scan.error_message && (
                      <div className="mt-3 text-sm text-danger-red bg-red-50 border border-red-200 rounded px-3 py-2">
                        Error: {scan.error_message}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="flex items-center space-x-2 ml-4">
                  {(scan.status === 'running' || scan.status === 'pending') && (
                    <button
                      onClick={(e) => handleCancelClick(scan.id, e)}
                      className="flex items-center space-x-2 px-3 py-2 rounded bg-yellow-900/10 text-yellow-600 hover:bg-yellow-900/20 transition-colors text-sm"
                      title="Stop Scan"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Stop Scan</span>
                    </button>
                  )}
                  <button
                    onClick={(e) => handleDeleteClick(scan.id, e)}
                    className="flex items-center space-x-2 px-3 py-2 rounded bg-danger-red/10 text-danger-red hover:bg-danger-red/20 transition-colors text-sm"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scan Modal */}
      <ScanModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveScan}
        targets={target ? [target] : []}
        modules={modules}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmAction.isOpen}
        onClose={() => setConfirmAction({ isOpen: false, type: null, scanId: null })}
        onConfirm={handleConfirm}
        title={confirmAction.type === 'cancel' ? 'Cancel Scan' : 'Delete Scan'}
        message={confirmAction.type === 'cancel' 
          ? 'Are you sure you want to cancel this scan? This action cannot be undone.'
          : 'Are you sure you want to delete this scan? This action cannot be undone.'}
        confirmText={confirmAction.type === 'cancel' ? 'Cancel Scan' : 'Delete'}
        cancelText="Cancel"
        variant={confirmAction.type === 'cancel' ? 'warning' : 'danger'}
      />
      </div>
    </div>
  );
};

export default TargetDetail;
